/**
 * The render stage: WebGL context, scene, camera, and resize handling.
 *
 * Deliberately thin. The original's five-pass deferred pipeline (depth pre-pass, ambient,
 * lighting with shadows, post-processing, 2D overlay — see docs/original-engine/renderer.md)
 * is Phase 4 stream 9 and slots in behind this interface. What exists here is the context
 * and the frame boundary, so everything else has something to draw into.
 */

import { LinearSRGBColorSpace, PerspectiveCamera, Scene, WebGLRenderer } from 'three';

export interface StageCapabilities {
  webgl2: boolean;
  /**
   * Required by the original's lighting stage, which keeps five full-resolution
   * high-precision buffers per view. Standard on desktop, less certain on older mobile.
   * When absent we will need the reduced-precision path (not yet written).
   */
  colorBufferFloat: boolean;
  maxTextureSize: number;
  maxDrawBuffers: number;
  renderer: string;
}

export class Stage {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly capabilities: StageCapabilities;

  private readonly canvas: HTMLCanvasElement;
  private readonly resizeObserver: ResizeObserver;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: false, // the original does edge smoothing in-shader; see renderer.md
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // postprocess.frag does its own sRGB encode, in the place the original's
    // GL_FRAMEBUFFER_SRGB did it. three must not apply a second one on top.
    this.renderer.outputColorSpace = LinearSRGBColorSpace;

    const gl = this.renderer.getContext();
    const gl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext
      ? gl
      : null;
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    this.capabilities = {
      webgl2: gl2 !== null,
      // Every render target in the deferred pipeline is RGBA16F. That is a legal texture
      // format in core WebGL2 but is not colour-renderable without an extension, and
      // EITHER of these two grants it — three.js treats them as interchangeable for
      // half-float targets. Probing only the first one reports devices as unsupported
      // that in fact render the whole pipeline correctly.
      colorBufferFloat:
        gl.getExtension('EXT_color_buffer_float') !== null ||
        gl.getExtension('EXT_color_buffer_half_float') !== null,
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
      // Multiple draw buffers are what make the lighting stage's five simultaneous
      // targets possible in one pass. WebGL1 has none.
      maxDrawBuffers: gl2 ? (gl2.getParameter(gl2.MAX_DRAW_BUFFERS) as number) : 1,
      renderer: debugInfo
        ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
        : 'unknown',
    };

    this.camera = new PerspectiveCamera(38, 1, 0.5, 400);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  private resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.renderer.dispose();
  }
}
